<script setup>
import { computed, inject } from 'vue'
import { loc } from '@/__core__/localization/localization'
import FALLBACK_IMAGE from '@/__core__/assets/sprites/fallback-player-avatar.svg'
import PersonCardSkeleton from '@/__v1.5__/components/cards/PersonCardSkeleton.vue'
import PlayerEvents from '@/__v1.5__/components/player-events/PlayerEvents.vue'
import SzwImage from '@/__v1.5__/components/image/Image.vue'

/** @typedef {{ content: string, type: 'substitute-in' | 'substitute-out' | 'own-goal' | 'goal' | 'yellow-card' | 'red-card' }} Event */
const props = defineProps({
  cardType: {
    /** @type import('vue').PropType<'player' | 'manager'> */
    type: String,
    default: 'player'
  },
  jersey: [Number, String],
  logo: String,
  teamName: {
    type: String,
    default: ''
  },
  detail: String,
  events: {
    /** @type import('vue').PropType<Event[]> */
    type: Array,
    default: () => []
  },
  title: String,
  subtitle: String,
  cursor: Boolean
})

const status = inject('status')
const langId = inject('langId')

const emit = defineEmits(['card-click'])

const localizedDetail = computed(() => loc(`sm_${props.detail}`, langId))

function emitCardClick() {
  emit('card-click')
}
</script>

<template>
  <person-card-skeleton v-if="status === 'loading'" />

  <div
    v-else
    :class="['szw-flex szw-h-[72px] szw-w-full szw-p-[3px] szw-gap-2 szw-rounded-2', { 'szw-cursor-pointer': cursor }]"
    style="border: 1px solid var(--szw-border)"
    @click="emitCardClick"
  >
    <div class="szw-w-[64px] szw-h-[64px] szw-relative szw-rounded-1">
      <div class="szw-w-[64px] szw-h-[64px] szw-rounded-1 szw-absolute szw-z-0 szw-left-0 szw-top-0 szw-overflow-hidden">
        <div class="skewed-div" />
      </div>

      <div class="szw-flex szw-items-center szw-justify-center szw-h-sizing-md szw-w-sizing-md szw-rounded-full szw-absolute -szw-right-[8px] szw-top-[19px] szw-z-50 szw-p-0.5">
        <szw-image :src="logo" class="szw-h-full szw-w-full" alt="team-logo" :fallback-content="teamName" />
      </div>

      <div class="szw-absolute szw-z-50 szw-left-0 szw-top-0 szw-w-[52px] szw-h-full szw-flex szw-justify-center szw-items-center szw-overflow-hidden">
        <span v-if="cardType === 'player' && props.jersey" class="szw-text-font-size-2xl szw-leading-font-line-height-2xl szw-font-bold szw-text-text-icon-primary">{{
          jersey
        }}</span>
        <div
          v-else
          class="szw-w-[56px] szw-h-full szw-absolute szw-left-0 szw-top-0 szw-z-10 szw-bg-no-repeat"
          :style="{ backgroundImage: `url(${FALLBACK_IMAGE})`, backgroundSize: '41px', backgroundPosition: '4px bottom' }"
        />
      </div>
    </div>

    <div class="szw-flex szw-flex-col szw-justify-center szw-ml-2" style="width: calc(100% - 80px)">
      <span v-if="detail" class="szw-text-font-size-xs szw-leading-font-line-height-xs szw-uppercase szw-font-bold szw-tracking-[4%]" style="color: #ea681f">{{
        localizedDetail
      }}</span>

      <player-events v-if="events" :events="events" />

      <span class="szw-text-font-size-md szw-font-medium szw-leading-font-line-height-md szw-tracking-[0%] szw-truncate">{{ title }}</span>
      <span v-show="subtitle" class="szw-text-font-size-sm szw-leading-font-line-height-sm szw-tracking-[0%]">{{ subtitle }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.skewed-div {
  @apply szw-absolute szw-z-0 -szw-left-[16px] szw-top-0;

  width: 64px;
  height: 64px;
  margin: 0;
  border-radius: 4px;
  overflow: hidden;
  transform: skewX(-15deg);
  transform-origin: 0 100%;

  background: var(--szw-background-color-secondary);
}
</style>
